<template>
  <ValidationObserver v-slot="{ handleSubmit }" ref="observer">
    <loading :active.sync="isLoading" :is-full-page="false" />

    <form @submit.prevent="handleSubmit(submitStep)" class="px-6">
      <div class="mb-6 border-b">
        <TCheckbox
          v-model="skipOperatorApp"
          :label="
            $t(
              'components.fleetManagement.addEdit.steps.operator.title.doNotNeedOperatorConfigCheckbox'
            )
          "
          :name="
            $t(
              'components.fleetManagement.addEdit.steps.operator.title.doNotNeedOperatorConfigCheckbox'
            )
          "
          wrapped
        />
      </div>

      <AppInput
        v-model="form.charging_task.rate"
        type="number"
        step="0.01"
        rules=""
        :label="
          $t(
            'components.fleetManagement.addEdit.steps.operator.title.chargingEarningRate'
          )
        "
        :name="
          $t(
            'components.fleetManagement.addEdit.steps.operator.title.chargingEarningRate'
          )
        "
        placeholder="e.g. 20"
        :disabled="skipOperatorApp"
        :haveUnitText="true"
        :unitText="currencySymbol"
      />

      <text-input
        rules=""
        :label="
          $t(
            'components.fleetManagement.addEdit.steps.operator.title.chargingPoint'
          )
        "
        :name="
          $t(
            'components.fleetManagement.addEdit.steps.operator.title.chargingPoint'
          )
        "
        placeholder="e.g. 5"
        v-model="form.charging_task.point"
        :disabled="skipOperatorApp"
      />

      <AppInput
        v-model="form.rebalance_task.rate"
        type="number"
        step="0.01"
        rules=""
        :label="
          $t(
            'components.fleetManagement.addEdit.steps.operator.title.rebalanceEarningRate'
          )
        "
        :name="
          $t(
            'components.fleetManagement.addEdit.steps.operator.title.rebalanceEarningRate'
          )
        "
        placeholder="e.g. 20"
        :disabled="skipOperatorApp"
        :haveUnitText="true"
        :unitText="currencySymbol"
      />

      <text-input
        rules=""
        :label="
          $t(
            'components.fleetManagement.addEdit.steps.operator.title.rebalancePoint'
          )
        "
        :name="
          $t(
            'components.fleetManagement.addEdit.steps.operator.title.rebalancePoint'
          )
        "
        placeholder="e.g. 5"
        v-model="form.rebalance_task.point"
        :disabled="skipOperatorApp"
      />

      <button type="submit" ref="submitButton" class="hidden">
        Save
      </button>
    </form>
  </ValidationObserver>
</template>

<script>
import { useEndpoints } from '@/composables'
import { deepCompareObjects } from '@/utils'

import TextInput from '@/components/form/TextInput'

export default {
  name: 'Step5',

  components: {
    TextInput,
  },

  props: {
    primaryKey: {
      required: true,
    },
    formData: {
      required: false,
    },
    isEditing: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    return {
      isLoading: false,

      skipOperatorApp: false,

      form: {
        charging_task: {
          rate: '',
          point: '',
        },

        rebalance_task: {
          rate: '',
          point: '',
        },
      },
      permanentAddressIsSame: false,
      currencySymbol: '',
    }
  },

  watch: {
    // sync props.formData with $data.from
    formData: {
      immediate: true,
      deep: true,
      handler(data) {
        if (data) {
          this.form = { ...data.data }
          this.currencySymbol = data.currencySymbol
        }
      },
    },
    // notify form is dirty & user should confirm before exiting
    form: {
      immediate: false,
      deep: true,
      handler(updatedFormData) {
        // don't notify if editing data & user input data are same
        if (deepCompareObjects(this.formData, updatedFormData)) {
          return
        }
        // otherwise notify whenever there's new input / change
        this.$emit('dirty', { step: 5, data: this.form })
      },
    },
  },

  methods: {
    async submit() {
      await this.$refs.submitButton.click()
    },
    async submitStep() {
      if (this.skipOperatorApp) {
        this.$emit('save', { step: 5 })
        return
      }

      // charging
      const chargingUrl = useEndpoints.fleet.operator.charging(this.primaryKey)

      const chargingData = new FormData()
      for (const k in this.form.charging_task) {
        chargingData.append(k, this.form.charging_task[k])
      }

      const chargingReq = this.$http.patch(chargingUrl, chargingData)

      // rebalance
      const rebalanceUrl = useEndpoints.fleet.operator.rebalance(
        this.primaryKey
      )

      const rebalanceData = new FormData()
      for (const k in this.form.rebalance_task) {
        rebalanceData.append(k, this.form.rebalance_task[k])
      }

      const rebalanceReq = this.$http.patch(rebalanceUrl, rebalanceData)

      // submit
      this.isLoading = true
      await this.$http
        .all([chargingReq, rebalanceReq])
        .then(
          this.$http.spread((...responses) => {
            const [chargingRes, rebalanceRes] = responses

            const data = {
              charging_task: chargingRes.data,
              rebalance_task: rebalanceRes.data,
            }

            console.log('submit3', data)

            this.$emit('save', { step: 5, data })

            const message = `Fleet ${
              this.isEditing ? 'updated' : 'added'
            } successfully`

            this.$notify({
              group: 'bottomLeft',
              type: 'success',
              title: 'Success',
              text: message,
            })
          })
        )
        .catch((err) => {
          // todo: emit fail?
          console.error('Error occured!', err)
          this.$notify({
            group: 'bottomLeft',
            type: 'error',
            title: 'Error occured!',
            text: err.response.data.message,
          })
        })
        .finally(() => (this.isLoading = false))
    },
  },
}
</script>

<style lang="scss" scoped>
h1 {
  font-size: 22px;
  font-weight: bold;
  color: #2e2e39;
}
</style>
